import React, { useState } from "react";
import { Modal, Button, Typography, notification, Slider } from "antd";
import { TbArrowUp, TbArrowDown, TbClock, TbCalendarStats, TbCircleCheck, TbAlertTriangle } from "react-icons/tb";
import { IChannel } from "dtos/communication-channel.dto";
import { ApiService } from "api/api.service";

const { Text } = Typography;

type SyncDirection = "forward" | "backward";

interface SyncModalProps {
  open: boolean;
  onClose: () => void;
  channel: IChannel;
  deskId: string;
}

const PRESET_OPTIONS = [
  { months: 1, label: "1 month", color: "#10b981", bg: "#ecfdf5", border: "#6ee7b7" },
  { months: 2, label: "2 months", color: "#3b82f6", bg: "#eff6ff", border: "#93c5fd" },
  { months: 3, label: "3 months", color: "#8b5cf6", bg: "#f5f3ff", border: "#c4b5fd" },
  { months: 4, label: "4 months", color: "#f59e0b", bg: "#fffbeb", border: "#fcd34d" },
  { months: 5, label: "5 months", color: "#ef4444", bg: "#fef2f2", border: "#fca5a5" },
  { months: 6, label: "6 months", color: "#6366f1", bg: "#eef2ff", border: "#a5b4fc" },
];

const formatDate = (date: Date): string =>
  date.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

const subtractMonths = (date: Date, months: number): Date => {
  const d = new Date(date);
  d.setMonth(d.getMonth() - months);
  return d;
};

const SyncModal: React.FC<SyncModalProps> = ({ open, onClose, channel, deskId }) => {
  const [direction, setDirection] = useState<SyncDirection | null>(null);
  const [monthsBack, setMonthsBack] = useState<number | null>(null);
  const syncChannels = ApiService.communicationChannel.useSyncChannel();

  const now = new Date();
  if (!channel) return null;
  const selectedPreset = PRESET_OPTIONS.find((p) => p.months === monthsBack) ?? null;
  const backfillFrom = monthsBack ? subtractMonths(now, monthsBack) : null;
  const backfillUntil = channel.oldestSyncedAt ? new Date(channel.oldestSyncedAt) : now;
  const isBackfillValid = backfillFrom ? backfillFrom < backfillUntil : false;

  const resolvedLabel = (() => {
    if (direction === "forward") {
      if (!channel.lastSyncedAt) return { text: "Syncing last 90 days → now", valid: true };
      return {
        text: `Syncing from ${formatDate(new Date(channel.lastSyncedAt))} → now`,
        valid: true,
      };
    }
    if (direction === "backward" && backfillFrom) {
      if (!isBackfillValid) {
        return { text: "Already synced up to this point — pick a longer range", valid: false };
      }
      return {
        text: `Syncing from ${formatDate(backfillFrom)} → ${formatDate(backfillUntil)}`,
        valid: true,
      };
    }
    return null;
  })();

  const canSync =
    direction !== null &&
    (direction === "forward" || (direction === "backward" && monthsBack !== null && isBackfillValid));

  const handleSync = () => {
    const payload: { desk_id: string; channel_id: string; syncFrom?: string } = {
      desk_id: deskId,
      channel_id: channel._id,
    };

    if (direction === "backward" && backfillFrom) {
      payload.syncFrom = backfillFrom.toISOString();
    }

    syncChannels.mutate(payload, {
      onSuccess: () => {
        notification.info({ message: "Sync job queued" });
        onClose();
        setDirection(null);
        setMonthsBack(null);
      },
    });
  };

  const handleClose = () => {
    onClose();
    setDirection(null);
    setMonthsBack(null);
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 8,
              background: "#f5f3ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <TbCalendarStats size={17} color="#7c3aed" />
          </div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#111827", lineHeight: 1.3 }}>Sync channel</div>
            <div style={{ fontSize: 11.5, color: "#9ca3af", fontWeight: 400 }}>{channel.alias ?? channel.label}</div>
          </div>
        </div>
      }
      width={440}
      styles={{ body: { paddingTop: 4 } }}
    >
      {/* Direction cards */}
      <div style={{ display: "flex", gap: 10, marginBottom: 18 }}>
        {(
          [
            {
              value: "forward" as SyncDirection,
              icon: <TbArrowUp size={16} />,
              iconBg: "#ecfdf5",
              iconColor: "#10b981",
              selectedIconBg: "#10b981",
              selectedBorder: "#10b981",
              selectedBg: "#f0fdf4",
              label: "Forward",
              description: "Catch up on new emails since last sync",
            },
            {
              value: "backward" as SyncDirection,
              icon: <TbArrowDown size={16} />,
              iconBg: "#eff6ff",
              iconColor: "#3b82f6",
              selectedIconBg: "#7c3aed",
              selectedBorder: "#7c3aed",
              selectedBg: "#faf5ff",
              label: "Backward",
              description: "Fill historical gaps before oldest sync",
            },
          ] as const
        ).map((opt) => {
          const isSelected = direction === opt.value;
          return (
            <button
              key={opt.value}
              onClick={() => {
                setDirection(opt.value);
                if (opt.value === "forward") setMonthsBack(null);
              }}
              style={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-start",
                gap: 8,
                padding: "13px 14px",
                borderRadius: 10,
                border: `1.5px solid ${isSelected ? opt.selectedBorder : "#e5e7eb"}`,
                background: isSelected ? opt.selectedBg : "#fafafa",
                cursor: "pointer",
                transition: "all 0.15s",
                textAlign: "left",
              }}
            >
              <div
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 7,
                  background: isSelected ? opt.selectedIconBg : opt.iconBg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: isSelected ? "#fff" : opt.iconColor,
                  transition: "all 0.15s",
                }}
              >
                {opt.icon}
              </div>
              <div>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: isSelected ? (opt.value === "forward" ? "#065f46" : "#5b21b6") : "#111827",
                    marginBottom: 2,
                  }}
                >
                  {opt.label}
                </div>
                <div style={{ fontSize: 11, color: "#9ca3af", lineHeight: 1.4 }}>{opt.description}</div>
              </div>
            </button>
          );
        })}
      </div>

      {/* Preset month chips — only for backward */}
      {direction === "backward" && (
        <div style={{ marginBottom: 18 }}>
          <div
            style={{
              fontSize: 11.5,
              fontWeight: 500,
              color: "#6b7280",
              marginBottom: 10,
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            How far back?
          </div>

          {/* Quick suggestion chips */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
            {PRESET_OPTIONS.filter((p) => [3, 4, 5, 6].includes(p.months)).map((preset) => {
              const isSelected = monthsBack === preset.months;
              return (
                <button
                  key={preset.months}
                  onClick={() => setMonthsBack(preset.months)}
                  style={{
                    padding: "5px 12px",
                    borderRadius: 20,
                    border: `1.5px solid ${isSelected ? preset.color : "#e5e7eb"}`,
                    background: isSelected ? preset.bg : "#fff",
                    color: isSelected ? preset.color : "#6b7280",
                    fontSize: 12,
                    fontWeight: isSelected ? 600 : 400,
                    cursor: "pointer",
                    transition: "all 0.12s",
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                  }}
                >
                  {isSelected && <TbCircleCheck size={12} />}
                  {preset.label}
                </button>
              );
            })}
          </div>

          {/* Slider */}
          <div
            style={{
              background: "#f9fafb",
              borderRadius: 10,
              padding: "16px 18px",
              border: "1px solid #f0f0f8",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
              <Text style={{ fontSize: 12.5, fontWeight: 500, color: "#374151" }}>Custom range</Text>
              <Text style={{ fontSize: 12.5, fontWeight: 600, color: "#7c3aed" }}>
                {monthsBack ? `${monthsBack} ${monthsBack === 1 ? "month" : "months"}` : "—"}
              </Text>
            </div>
            <Slider
              min={1}
              max={6}
              step={1}
              value={monthsBack ?? 1}
              onChange={(val) => setMonthsBack(val)}
              tooltip={{ formatter: (v) => `${v}mo` }}
              styles={{
                track: { background: "#7c3aed" },
                handle: { borderColor: "#7c3aed" },
              }}
              marks={{
                1: <span style={{ fontSize: 10, color: "#9ca3af" }}>1mo</span>,
                6: <span style={{ fontSize: 10, color: "#9ca3af" }}>6mo</span>,
              }}
            />
          </div>
        </div>
      )}

      {/* Resolved range label */}
      {resolvedLabel && (
        <div
          style={{
            marginBottom: 16,
            padding: "9px 13px",
            borderRadius: 8,
            background: resolvedLabel.valid ? "#f5f3ff" : "#fff7ed",
            border: `1px solid ${resolvedLabel.valid ? "#ede9fe" : "#fed7aa"}`,
            display: "flex",
            alignItems: "center",
            gap: 7,
          }}
        >
          {resolvedLabel.valid ? (
            <TbCircleCheck size={14} color="#7c3aed" style={{ flexShrink: 0 }} />
          ) : (
            <TbAlertTriangle size={14} color="#d97706" style={{ flexShrink: 0 }} />
          )}
          <Text
            style={{
              fontSize: 12,
              color: resolvedLabel.valid ? "#5b21b6" : "#92400e",
            }}
          >
            {resolvedLabel.text}
          </Text>
        </div>
      )}

      {/* Cursor info row */}
      <div
        style={{
          display: "flex",
          gap: 0,
          marginBottom: 20,
          borderRadius: 9,
          overflow: "hidden",
          border: "1px solid #f0f0f8",
        }}
      >
        <div
          style={{
            flex: 1,
            padding: "10px 14px",
            background: "#fafafa",
            display: "flex",
            alignItems: "center",
            gap: 9,
          }}
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 6,
              background: "#eff6ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <TbClock size={13} color="#3b82f6" />
          </div>
          <div>
            <div style={{ fontSize: 10.5, color: "#9ca3af", marginBottom: 1 }}>Oldest synced</div>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: "#111827" }}>
              {channel.oldestSyncedAt ? formatDate(new Date(channel.oldestSyncedAt)) : "—"}
            </div>
          </div>
        </div>

        <div style={{ width: 1, background: "#f0f0f8" }} />

        <div
          style={{
            flex: 1,
            padding: "10px 14px",
            background: "#fafafa",
            display: "flex",
            alignItems: "center",
            gap: 9,
          }}
        >
          <div
            style={{
              width: 28,
              height: 28,
              borderRadius: 6,
              background: "#ecfdf5",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <TbArrowUp size={13} color="#10b981" />
          </div>
          <div>
            <div style={{ fontSize: 10.5, color: "#9ca3af", marginBottom: 1 }}>Last synced</div>
            <div style={{ fontSize: 12.5, fontWeight: 600, color: "#111827" }}>
              {channel.lastSyncedAt ? formatDate(new Date(channel.lastSyncedAt)) : "Never"}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          type="primary"
          disabled={!canSync}
          loading={syncChannels.isPending}
          onClick={handleSync}
          style={{
            background: canSync ? "#7c3aed" : undefined,
            borderColor: canSync ? "#7c3aed" : undefined,
          }}
        >
          Start Sync
        </Button>
      </div>
    </Modal>
  );
};

export default SyncModal;
